import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { environment } from '@env/environment';
import {
  AuthService,
  Course,
  CourseLog,
  CoursePaidBy,
  CourseStatus,
  ICourse,
  ICourseLog,
  IUser,
  User,
} from '@app/core';
import { ReportPermissionResponse } from '@games/entities/report.entity';

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  coursesSubject: BehaviorSubject<{ [id: number]: Course }>;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.coursesSubject = new BehaviorSubject<{ [id: number]: Course }>(null);
  }

  getCourse(id: number): Observable<Course> {
    if (this.coursesSubject.value && id in this.coursesSubject.value) {
      return this.coursesSubject.pipe(
        map(courses => courses[id]),
        first()
      );
    }

    return this.http.get<any>(`${environment.apiUrl}courses/${id}/`).pipe(
      tap((course: ICourse) =>
        this.coursesSubject.next({
          ...(this.coursesSubject.value ? this.coursesSubject.value : {}),
          [id]: new Course(course),
        })
      ),
      map((course: ICourse) => new Course(course))
    );
  }

  getCourseUsers(
    id: number,
    filters: HttpParams | { [params: string]: string | string[] } = {},
    isSilentLoader = false
  ): Observable<any> {
    return this.http
      .get<any>(`${environment.apiUrl}courses/${id}/users/`, {
        params: filters,
        headers: { 'silent-loader': isSilentLoader ? 'true' : 'false' },
      })
      .pipe(
        map(res => ({
          ...res,
          results: res.results.map(
            (result: { user: IUser; userCourse: { paid: boolean; status: number } }) => ({
              ...result,
              user: new User({
                ...result.user,
                userCourses: [{ ...result.userCourse, course: id }],
              }),
            })
          ),
        }))
      );
  }

  getPayments() {
    return of([
      { id: CoursePaidBy.Institute, name: 'Institute' },
      { id: CoursePaidBy.Player, name: 'Player' },
    ]);
  }

  getPaymentStatuses() {
    return of([
      { id: true, name: 'Paid' },
      { id: false, name: 'Unpaid' },
    ]);
  }

  getStatuses() {
    const statuses = [
      { id: CourseStatus.Inactive, name: 'Inactive' },
      { id: CourseStatus.Active, name: 'Active' },
    ];

    if (this.authService.user.isAdmin) {
      statuses.push({ id: CourseStatus.Archived, name: 'Archived' });
    }

    return of(statuses);
  }

  getDatabaseDetail(id: number) {
    return this.http.get<any>(`${environment.apiUrl}courses/${id}/course_databases/`);
  }

  createCourse(course: ICourse) {
    return this.http.post<any>(`${environment.apiUrl}courses/`, course).pipe(
      tap((course: ICourse) =>
        this.coursesSubject.next({
          ...(this.coursesSubject.value ? this.coursesSubject.value : {}),
          [course.id]: new Course(course),
        })
      )
    );
  }

  updateCourse(course: ICourse) {
    return this.http.put<any>(`${environment.apiUrl}courses/${course.id}/`, course).pipe(
      tap((course: ICourse) =>
        this.coursesSubject.next({
          ...(this.coursesSubject.value ? this.coursesSubject.value : {}),
          [course.id]: new Course(course),
        })
      ),
      map((res: ICourse) => new Course(res))
    );
  }

  updateCourseStatus(
    id: number,
    status: CourseStatus.Active | CourseStatus.Archived | CourseStatus.Inactive
  ) {
    return this.http.patch<any>(`${environment.apiUrl}courses/${id}/`, { status }).pipe(
      tap((course: ICourse) =>
        this.coursesSubject.next({
          ...(this.coursesSubject.value ? this.coursesSubject.value : {}),
          [course.id]: new Course(course),
        })
      ),
      map((res: ICourse) => new Course(res))
    );
  }

  updateCourseStatuses(
    ids: number[],
    status: CourseStatus.Active | CourseStatus.Archived | CourseStatus.Inactive
  ) {
    return this.http.patch<any>(`${environment.apiUrl}courses/batch_edit/`, {
      courses: ids,
      updates: { status },
    });
  }

  deleteCourse(id: number) {
    if (this.coursesSubject.value && id in this.coursesSubject.value) {
      const courses = this.coursesSubject.value;

      delete courses[id];
      setTimeout(() => this.coursesSubject.next(courses));
    }

    return this.http.delete<any>(`${environment.apiUrl}courses/${id}/`, { observe: 'response' });
  }

  respondCourseRequests(response: { key: string; accept: boolean; reason?: string }[]) {
    return this.http.post<any>(`${environment.apiUrl}usercourses/accept/`, response);
  }

  addUsers(id: number, config: { users: number[]; delete?: boolean }) {
    return this.http.post<any>(`${environment.apiUrl}courses/${id}/invite/`, config);
  }

  getCourseLogs(
    id: number,
    filters: HttpParams | { [params: string]: string | string[] } = {},
    isSilentLoader = false
  ) {
    return this.http
      .get<any>(`${environment.apiUrl}courses/${id}/logs/`, {
        params: filters,
        headers: { 'silent-loader': isSilentLoader ? 'true' : 'false' },
      })
      .pipe(
        map(res => ({
          ...res,
          results: res.results.map((courseLog: ICourseLog) => new CourseLog(courseLog)),
        }))
      );
  }

  getReportPermissions(courseId: number): Observable<ReportPermissionResponse> {
    return this.http.get<ReportPermissionResponse>(
      `${environment.apiUrl}courses/${courseId}/report-permissions/`
    );
  }

  updateReportPermissions(
    courseId: number,
    reportId: number,
    groupPermissions: number[]
  ): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}courses/${courseId}/report-permissions/${reportId}/update_permissions/`,
      { group_permissions: groupPermissions }
    );
  }
}
